import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { useShopDataQuery } from "@/query/shopDataQuery"
import { 
  useCancellationFeeMutation,
  usePercentagePrepaymentMutation,
  useCanReserveWithoutPaymentMutation,
  useCancellationNoticeDaysMutation
} from "@/query/reservationOptionsQuery"
import { 
  CalendarIcon, 
  TokensIcon, 
  ExclamationTriangleIcon,
  CheckCircledIcon 
} from "@radix-ui/react-icons"
import React, { useState, useEffect } from "react"

type EditReservationOptionsModalProps = {
  isOpen: boolean
  onClose: () => void
}

export const EditReservationOptionsModal: React.FC<EditReservationOptionsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { shopData } = useShopDataQuery()
  const { cancellationFeeMutation } = useCancellationFeeMutation()
  const { percentagePrepaymentMutation } = usePercentagePrepaymentMutation()
  const { canReserveWithoutPaymentMutation } = useCanReserveWithoutPaymentMutation()
  const { cancellationNoticeDaysMutation } = useCancellationNoticeDaysMutation()

  // États locaux avec valeurs par défaut
  const [cancellationFee, setCancellationFee] = useState<number>(0)
  const [cancellationNoticeDays, setCancellationNoticeDays] = useState<number>(1)
  const [percentagePrepayment, setPercentagePrepayment] = useState<number>(0)
  const [canReserveWithoutPayment, setCanReserveWithoutPayment] = useState<boolean>(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set())

  // Initialiser les valeurs à partir des données du shop
  useEffect(() => {
    if (shopData?.bookingOptions) {
      setCancellationFee(shopData.bookingOptions.cancellationFee || 0)
      setCancellationNoticeDays(shopData.bookingOptions.cancellationNoticeDays || 1)
      setPercentagePrepayment(shopData.bookingOptions.percentagePrepayment || 0)
      setCanReserveWithoutPayment(shopData.bookingOptions.canReserveWithoutPayment || false)
    }
  }, [shopData])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (cancellationFee < 0) {
      newErrors.cancellationFee = "Les frais d'annulation ne peuvent pas être négatifs"
    }
    if (cancellationFee > 1000) {
      newErrors.cancellationFee = "Les frais d'annulation ne peuvent pas dépasser 1000€"
    }

    if (cancellationNoticeDays < 0) {
      newErrors.cancellationNoticeDays = "Le délai d'annulation ne peut pas être négatif"
    }
    if (cancellationNoticeDays > 30) {
      newErrors.cancellationNoticeDays = "Le délai d'annulation ne peut pas dépasser 30 jours"
    }

    if (percentagePrepayment < 0) {
      newErrors.percentagePrepayment = "Le pourcentage de prépaiement ne peut pas être négatif"
    }
    if (percentagePrepayment > 100) {
      newErrors.percentagePrepayment = "Le pourcentage de prépaiement ne peut pas dépasser 100%"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = () => {
    if (!validateForm()) {
      return
    }

    // Envoyer uniquement les champs modifiés
    if (editedFields.has('cancellationFee')) {
      cancellationFeeMutation({ cancellationFee })
    }
    if (editedFields.has('cancellationNoticeDays')) {
      cancellationNoticeDaysMutation({ cancellationNoticeDays })
    }
    if (editedFields.has('percentagePrepayment')) {
      percentagePrepaymentMutation({ percentagePrepayment })
    }
    if (editedFields.has('canReserveWithoutPayment')) {
      canReserveWithoutPaymentMutation({ canReserveWithoutPayment })
    }

    onClose()
  }

  const handleFieldChange = (field: string) => {
    setEditedFields(prev => new Set(prev).add(field))
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2 text-indigo-600" />
            Modifier les Options de Réservation
          </DialogTitle>
          <DialogDescription>
            Configurez les paramètres de réservation pour votre boutique.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Frais d'annulation */}
          <div className="space-y-3">
            <Label htmlFor="cancellationFee" className="flex items-center">
              <TokensIcon className="h-4 w-4 mr-2 text-slate-600" />
              Montant des frais d'annulation (€)
            </Label>
            <Input
              id="cancellationFee"
              type="number"
              min="0"
              max="1000"
              step="0.5"
              value={cancellationFee}
              onChange={(e) => {
                setCancellationFee(parseFloat(e.target.value) || 0)
                handleFieldChange('cancellationFee')
              }}
              placeholder="0 = Pas de frais"
              className={errors.cancellationFee ? "border-red-500" : ""}
            />
            {errors.cancellationFee && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {errors.cancellationFee}
              </p>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Frais appliqués en cas d'annulation tardive (0 = pas de frais)
            </p>
          </div>

          {/* Délai d'annulation */}
          <div className="space-y-3">
            <Label htmlFor="cancellationNoticeDays" className="flex items-center">
              <CalendarIcon className="h-4 w-4 mr-2 text-slate-600" />
              Délai d'annulation (jours)
            </Label>
            <Input
              id="cancellationNoticeDays"
              type="number"
              min="0"
              max="30"
              value={cancellationNoticeDays}
              onChange={(e) => {
                setCancellationNoticeDays(parseInt(e.target.value) || 1)
                handleFieldChange('cancellationNoticeDays')
              }}
              placeholder="1"
              className={errors.cancellationNoticeDays ? "border-red-500" : ""}
            />
            {errors.cancellationNoticeDays && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {errors.cancellationNoticeDays}
              </p>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Nombre de jours avant le rendez-vous pour annuler sans frais
            </p>
          </div>

          {/* Pourcentage de prépaiement */}
          <div className="space-y-3">
            <Label htmlFor="percentagePrepayment" className="flex items-center">
              <TokensIcon className="h-4 w-4 mr-2 text-slate-600" />
              Pourcentage de prépaiement (%)
            </Label>
            <Input
              id="percentagePrepayment"
              type="number"
              min="0"
              max="100"
              value={percentagePrepayment}
              onChange={(e) => {
                setPercentagePrepayment(parseInt(e.target.value) || 0)
                handleFieldChange('percentagePrepayment')
              }}
              placeholder="0 = Pas de prépaiement"
              className={errors.percentagePrepayment ? "border-red-500" : ""}
            />
            {errors.percentagePrepayment && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {errors.percentagePrepayment}
              </p>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Pourcentage du montant à payer lors de la réservation (0 = pas de prépaiement)
            </p>
          </div>

          {/* Réservation sans paiement */}
          <div className="flex items-center justify-between space-x-4">
            <div className="space-y-1">
              <Label htmlFor="canReserveWithoutPayment" className="flex items-center">
                <CheckCircledIcon className="h-4 w-4 mr-2 text-slate-600" />
                Autoriser la réservation sans paiement
              </Label>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Les clients peuvent réserver sans payer immédiatement
              </p>
            </div>
            <Switch
              id="canReserveWithoutPayment"
              checked={canReserveWithoutPayment}
              onCheckedChange={(checked) => {
                setCanReserveWithoutPayment(checked)
                handleFieldChange('canReserveWithoutPayment')
              }}
            />
          </div>

          {/* Message d'information */}
          {editedFields.size > 0 && (
            <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
              <ExclamationTriangleIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {editedFields.size} modification(s) en attente
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            disabled={editedFields.size === 0}
          >
            Enregistrer les modifications
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}